import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Globe, Download, Upload, Save, Search, ChevronRight, ChevronDown, Edit2, Check, X, FileText } from 'lucide-react';
import { API } from '../App';
import { getDefaultTranslations, loadTranslations as reloadGlobalTranslations } from '../i18n';
import { toast } from 'sonner';

// Deep merge: defaults first, overrides override leaf values (preserve missing nested keys)
function deepMerge(target, source) {
  const result = Array.isArray(target) ? [...target] : { ...target };
  if (!source || typeof source !== 'object') return result;
  for (const key of Object.keys(source)) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key]) && target[key] && typeof target[key] === 'object') {
      result[key] = deepMerge(target[key], source[key]);
    } else if (source[key] !== undefined && source[key] !== null) {
      result[key] = source[key];
    }
  }
  return result;
}

// Flatten nested object to dot-notation keys
function flattenObj(obj, prefix = '') {
  const result = {};
  for (const key of Object.keys(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
      Object.assign(result, flattenObj(obj[key], fullKey));
    } else {
      result[fullKey] = obj[key];
    }
  }
  return result;
}

// Unflatten dot-notation keys back to nested object
function unflattenObj(flat) {
  const result = {};
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split('.');
    let current = result;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) current[parts[i]] = {};
      current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = value;
  }
  return result;
}

const TranslationsTab = () => {
  const [activeLang, setActiveLang] = useState('fr');
  const [frData, setFrData] = useState({});
  const [enData, setEnData] = useState({});
  const [frFlat, setFrFlat] = useState({});
  const [enFlat, setEnFlat] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const fileInputRef = useRef(null);

  const fetchTranslations = useCallback(async () => {
    try {
      const [frRes, enRes] = await Promise.all([
        axios.get(`${API}/translations/fr`, { withCredentials: true }),
        axios.get(`${API}/translations/en`, { withCredentials: true })
      ]);
      // Deep merge defaults with DB overrides (preserves nested keys)
      const frDefaults = getDefaultTranslations('fr');
      const enDefaults = getDefaultTranslations('en');
      const frMerged = deepMerge(frDefaults, frRes.data || {});
      const enMerged = deepMerge(enDefaults, enRes.data || {});
      setFrData(frMerged);
      setEnData(enMerged);
      setFrFlat(flattenObj(frMerged));
      setEnFlat(flattenObj(enMerged));
    } catch {
      // Use defaults
      const frDefaults = getDefaultTranslations('fr');
      const enDefaults = getDefaultTranslations('en');
      setFrData(frDefaults);
      setEnData(enDefaults);
      setFrFlat(flattenObj(frDefaults));
      setEnFlat(flattenObj(enDefaults));
    }
  }, []);

  useEffect(() => { fetchTranslations(); }, [fetchTranslations]);

  const currentFlat = activeLang === 'fr' ? frFlat : enFlat;
  const setCurrentFlat = activeLang === 'fr' ? setFrFlat : setEnFlat;

  // Group keys by section
  const sections = {};
  Object.keys(currentFlat).forEach(key => {
    const section = key.split('.')[0];
    if (!sections[section]) sections[section] = [];
    sections[section].push(key);
  });

  const filteredSections = {};
  Object.entries(sections).forEach(([section, keys]) => {
    const filtered = keys.filter(key =>
      !searchTerm || key.toLowerCase().includes(searchTerm.toLowerCase()) || 
      String(currentFlat[key]).toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filtered.length > 0) filteredSections[section] = filtered;
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const startEdit = (key) => {
    setEditingKey(key);
    setEditValue(String(currentFlat[key] || ''));
  };

  const saveEdit = () => {
    if (editingKey) {
      setCurrentFlat(prev => ({ ...prev, [editingKey]: editValue }));
      setHasChanges(true);
      setEditingKey(null);
    }
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const saveToServer = async () => {
    const flat = activeLang === 'fr' ? frFlat : enFlat;
    if (!flat || Object.keys(flat).length === 0) {
      toast.error('Aucune donnee a sauvegarder. Rechargez la page si le probleme persiste.');
      return;
    }
    setSaving(true);
    try {
      const data = unflattenObj(flat);
      await axios.put(`${API}/admin/translations/${activeLang}`, data, { withCredentials: true });
      // Reload the global i18n cache so visitors see the new values immediately on next page load
      try { await reloadGlobalTranslations(); } catch (_) {}
      window.dispatchEvent(new Event('langchange'));
      toast.success(`Traductions ${activeLang.toUpperCase()} sauvegardees`);
      setHasChanges(false);
      if (activeLang === 'fr') setFrData(data);
      else setEnData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de sauvegarde');
    } finally { setSaving(false); }
  };

  const downloadFile = () => {
    const data = unflattenObj(currentFlat);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `translations_${activeLang}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (typeof data !== 'object') throw new Error('Invalid');

      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/admin/translations/${activeLang}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const flat = flattenObj(data);
      if (activeLang === 'fr') { setFrData(data); setFrFlat(flat); }
      else { setEnData(data); setEnFlat(flat); }
      setHasChanges(false);
      toast.success(`Fichier ${activeLang.toUpperCase()} importe avec succes`);
    } catch (err) {
      toast.error('Fichier JSON invalide');
    }
    e.target.value = '';
  };

  const sectionLabels = {
    categories: 'Categories', nav: 'Navigation', actions: 'Actions',
    header: 'Header', landing: 'Page d\'accueil', dashboard: 'Dashboard',
    messages: 'Messages', notifications: 'Notifications', shop: 'Boutique',
    account: 'Mon Compte', lost: 'Objets Perdus', ping: 'Page QR Scan',
    business: 'Entreprises', auth: 'Authentification', status: 'Statuts',
  };

  return (
    <div className="space-y-6" data-testid="translations-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-charcoal font-heading flex items-center gap-2">
            <Globe className="w-5 h-5" /> Gestion des Langues
          </h2>
          <p className="text-sm text-charcoal/50 mt-1">Modifiez, telechargez ou uploadez les fichiers de traduction</p>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <button onClick={saveToServer} disabled={saving} className="btn-primary flex items-center gap-2 text-sm" data-testid="save-translations-btn">
              <Save className="w-4 h-4" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          )}
          <button onClick={downloadFile} className="btn-outline text-sm flex items-center gap-2" data-testid="download-translations-btn">
            <Download className="w-4 h-4" /> Telecharger
          </button>
          <button onClick={() => fileInputRef.current?.click()} className="btn-outline text-sm flex items-center gap-2" data-testid="upload-translations-btn">
            <Upload className="w-4 h-4" /> Uploader
          </button>
          <input ref={fileInputRef} type="file" accept=".json" onChange={handleUpload} className="hidden" />
        </div>
      </div>

      {/* Language tabs */}
      <div className="flex items-center gap-3">
        {['fr', 'en'].map(lang => (
          <button
            key={lang}
            onClick={() => { setActiveLang(lang); setEditingKey(null); }}
            data-testid={`lang-tab-${lang}`}
            className={`px-6 py-2.5 rounded-full font-bold text-sm border-2 transition-all ${
              activeLang === lang
                ? 'bg-primary border-charcoal text-charcoal shadow-brutal'
                : 'bg-white border-charcoal/20 text-charcoal/60 hover:border-charcoal/40'
            }`}
          >
            {lang === 'fr' ? 'Francais' : 'English'}
            <span className="ml-2 text-xs opacity-50">({Object.keys(currentFlat).length} cles)</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal/40" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher une cle ou un texte..."
          className="input-brutal w-full pl-10"
          data-testid="search-translations"
        />
      </div>

      {/* Sections */}
      <div className="space-y-3">
        {Object.entries(filteredSections).map(([section, keys]) => (
          <div key={section} className="card-brutal overflow-hidden">
            <button
              onClick={() => toggleSection(section)}
              className="w-full flex items-center justify-between p-4 hover:bg-surface-alt/50 transition-colors"
              data-testid={`section-${section}`}
            >
              <div className="flex items-center gap-3">
                <FileText className="w-4 h-4 text-charcoal/50" />
                <span className="font-bold text-charcoal">{sectionLabels[section] || section}</span>
                <span className="text-xs text-charcoal/40 bg-surface-alt px-2 py-0.5 rounded-full">{keys.length} cles</span>
              </div>
              {expandedSections[section] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>

            {expandedSections[section] && (
              <div className="border-t-2 border-charcoal/10">
                {keys.map(key => {
                  const shortKey = key.replace(`${section}.`, '');
                  const value = currentFlat[key];
                  const isEditing = editingKey === key;

                  return (
                    <div
                      key={key}
                      className="flex items-start gap-3 px-4 py-2.5 border-b border-charcoal/5 hover:bg-surface-alt/30 transition-colors"
                      data-testid={`key-${key}`}
                    >
                      <code className="text-xs text-primary font-mono bg-primary/10 px-2 py-0.5 rounded shrink-0 mt-0.5 min-w-[120px]">
                        {shortKey}
                      </code>
                      <div className="flex-1 min-w-0">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                              className="input-brutal flex-1 text-sm py-1"
                              autoFocus
                              data-testid={`edit-input-${key}`}
                            />
                            <button onClick={saveEdit} className="p-1 text-green-600 hover:bg-green-50 rounded">
                              <Check className="w-4 h-4" />
                            </button>
                            <button onClick={cancelEdit} className="p-1 text-charcoal/40 hover:bg-surface-alt rounded">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 group cursor-pointer" onClick={() => startEdit(key)}>
                            <span className="text-sm text-charcoal flex-1 truncate">{String(value)}</span>
                            <Edit2 className="w-3 h-3 text-charcoal/20 group-hover:text-charcoal/60 shrink-0" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {Object.keys(filteredSections).length === 0 && (
        <div className="text-center py-12 text-charcoal/40">
          <Globe className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Aucun resultat pour "{searchTerm}"</p>
        </div>
      )}
    </div>
  );
};

export default TranslationsTab;
